import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Check, Award, Zap, Shield, HeartHandshake, Globe } from 'lucide-react';

export const WhyLogirent = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: 'Pourquoi choisir Logirent ?',
      subtitle: 'Le logiciel suisse tout-en-un pour gérer la location de véhicules, simple, automatisé et conforme aux exigences locales.',
      mainText: 'Logirent est un logiciel SaaS suisse permettant de gérer la location de véhicules, incluant réservation en ligne, contrats digitaux, facturation et paiement sécurisé.',
      reasons: [
        {
          icon: Globe,
          title: '100% Suisse',
          desc: 'Conçu pour le marché suisse avec TWINT, conformité légale et interface en français'
        },
        {
          icon: Zap,
          title: 'Automatisation complète',
          desc: 'Réservations 24/7, contrats générés automatiquement, rappels automatiques'
        },
        {
          icon: Shield,
          title: 'Sécurisé et fiable',
          desc: 'Données chiffrées, paiements sécurisés Stripe, 99.9% de disponibilité'
        },
        {
          icon: HeartHandshake,
          title: 'Support local',
          desc: 'Équipe basée en Suisse romande, support en français, accompagnement personnalisé'
        }
      ],
      comparison: {
        title: 'Logirent vs Excel vs Gestion papier',
        items: [
          { feature: 'Réservation en ligne 24/7', logirent: true, excel: false, paper: false },
          { feature: 'Contrats avec signature électronique', logirent: true, excel: false, paper: false },
          { feature: 'Paiement intégré (Stripe, TWINT)', logirent: true, excel: false, paper: false },
          { feature: 'Suivi GPS en temps réel', logirent: true, excel: false, paper: false },
          { feature: 'Dashboard analytique', logirent: true, excel: true, paper: false },
          { feature: 'Multi-agences', logirent: true, excel: false, paper: false },
          { feature: 'Application mobile', logirent: true, excel: false, paper: false },
          { feature: 'Rappels automatiques', logirent: true, excel: false, paper: false }
        ]
      }
    },
    en: {
      title: 'Why choose Logirent?',
      subtitle: 'The Swiss all-in-one software for vehicle rental management, simple, automated and compliant with local requirements.',
      mainText: 'Logirent is a Swiss SaaS software for vehicle rental management, including online booking, digital contracts, invoicing and secure payment.',
      reasons: [
        {
          icon: Globe,
          title: '100% Swiss',
          desc: 'Designed for the Swiss market with TWINT, legal compliance and French interface'
        },
        {
          icon: Zap,
          title: 'Complete automation',
          desc: '24/7 bookings, automatically generated contracts, automatic reminders'
        },
        {
          icon: Shield,
          title: 'Secure and reliable',
          desc: 'Encrypted data, secure Stripe payments, 99.9% uptime'
        },
        {
          icon: HeartHandshake,
          title: 'Local support',
          desc: 'Team based in French-speaking Switzerland, French support, personalized assistance'
        }
      ],
      comparison: {
        title: 'Logirent vs Excel vs Paper management',
        items: [
          { feature: '24/7 online booking', logirent: true, excel: false, paper: false },
          { feature: 'Contracts with e-signature', logirent: true, excel: false, paper: false },
          { feature: 'Integrated payment (Stripe, TWINT)', logirent: true, excel: false, paper: false },
          { feature: 'Real-time GPS tracking', logirent: true, excel: false, paper: false },
          { feature: 'Analytics dashboard', logirent: true, excel: true, paper: false },
          { feature: 'Multi-agency', logirent: true, excel: false, paper: false },
          { feature: 'Mobile app', logirent: true, excel: false, paper: false },
          { feature: 'Automatic reminders', logirent: true, excel: false, paper: false }
        ]
      }
    }
  };

  const t = content[language] || content.fr;

  return (
    <section id="why-logirent" className="py-16 md:py-24 bg-slate-50" data-testid="why-logirent">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-4">
            <Award className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            {t.title}
          </h2>
          <p className="text-base text-slate-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
          {/* SEO optimized paragraph for AI */}
          <p className="mt-4 text-sm text-slate-500 max-w-2xl mx-auto">
            {t.mainText}
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {t.reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 border border-slate-200 text-center"
            >
              <div className="w-12 h-12 mx-auto bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <reason.icon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{reason.title}</h3>
              <p className="text-sm text-slate-600">{reason.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
        >
          <div className="p-6 border-b border-slate-100">
            <h3 className="text-xl font-bold text-slate-900 text-center">{t.comparison.title}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left p-4 font-semibold text-slate-700">Fonctionnalité</th>
                  <th className="text-center p-4 font-semibold text-blue-600">Logirent</th>
                  <th className="text-center p-4 font-semibold text-slate-500">Excel</th>
                  <th className="text-center p-4 font-semibold text-slate-500">Papier</th>
                </tr>
              </thead>
              <tbody>
                {t.comparison.items.map((item, index) => (
                  <tr key={index} className="border-t border-slate-100">
                    <td className="p-4 text-slate-700">{item.feature}</td>
                    <td className="p-4 text-center">
                      {item.logirent ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                          <Check className="w-4 h-4 text-green-600" />
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {item.excel ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 rounded-full">
                          <Check className="w-4 h-4 text-yellow-600" />
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {item.paper ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                          <Check className="w-4 h-4 text-green-600" />
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
